import type { PosSettings, ReceiptData } from '@/types';

export function showReceiptDiscountPrint(posSettings?: PosSettings | null): boolean {
  return !posSettings || posSettings.show_discount_in_invoice_print !== false;
}

/** Loyalty redeem amount (prefer explicit loyalty_discount). */
export function receiptLoyaltyDiscount(
  inv:
    | {
        discount_amount?: number;
        loyalty?: { loyalty_discount?: number; redeemed?: number } | null;
      }
    | null
    | undefined,
): number {
  const explicit = Number(inv?.loyalty?.loyalty_discount) || 0;
  if (explicit > 0) return explicit;
  // Redeemed points but amount missing — treat whole discount as loyalty only when
  // there is no separate cart discount signal (redeemed > 0 and discount present).
  if ((Number(inv?.loyalty?.redeemed) || 0) > 0) {
    return Number(inv?.discount_amount) || 0;
  }
  return 0;
}

/** Cart discount excluding loyalty redeem (loyalty is stored inside discount_amount). */
export function receiptCartDiscount(
  inv:
    | {
        discount_amount?: number;
        loyalty?: { loyalty_discount?: number; redeemed?: number } | null;
      }
    | null
    | undefined,
): number {
  const total = Number(inv?.discount_amount) || 0;
  const loyalty = receiptLoyaltyDiscount(inv);
  if (loyalty > 0 && total >= loyalty - 0.01) {
    return Math.max(0, Math.round((total - loyalty) * 100) / 100);
  }
  return total;
}

export function receiptPromoTotals(
  inv: Pick<ReceiptData, 'item_total' | 'service_total' | 'promo_total'> | null | undefined,
) {
  const charged = (Number(inv?.item_total) || 0) + (Number(inv?.service_total) || 0);
  const promo = Number(inv?.promo_total) || 0;
  return { charged, promo, original: charged + promo };
}

export function receiptLinePromo(item: {
  quantity?: number;
  rate?: number;
  amount?: number;
  original_rate?: number;
  promo_amount?: number;
}) {
  const qty = Number(item.quantity) || 0;
  const chargedAmount = Number(item.amount) || 0;
  const chargedRate = Number(item.rate) || 0;
  const promoAmount = Number(item.promo_amount) || 0;
  const storedOriginalRate = Number(item.original_rate) || 0;
  const originalAmount = promoAmount > 0
    ? chargedAmount + promoAmount
    : storedOriginalRate > 0 && qty
      ? storedOriginalRate * qty
      : chargedAmount;
  const originalRate = storedOriginalRate > 0
    ? storedOriginalRate
    : qty
      ? originalAmount / qty
      : chargedRate;
  const hasPromo = promoAmount > 0.004 && originalAmount - chargedAmount > 0.004;
  return {
    chargedRate,
    chargedAmount,
    originalRate,
    originalAmount,
    promoAmount,
    hasPromo,
  };
}
